/**
 * The small pieces vB6 builds with a template literal in half a dozen places:
 * a row of choices, a note, a dish row, a category tile.
 *
 * Each one produces the markup vB6 produces, class for class, because the
 * stylesheet is vB6's and a renamed class is a rule that stops applying. The
 * `aria-pressed` on a choice, the `role="button"` and `tabindex` on a dish
 * row and the dashed tick on an unselected one are all load-bearing: §6 of
 * `docs/overnight_constraints.md` asks that the four states stay far apart,
 * and the stylesheet draws them off exactly these attributes.
 */

import type { ReactNode } from 'react'

import type { PageBundle } from './bundle.ts'
import type { Lang } from './lang.ts'
import { CATEGORY_TILES, CHEF_TILE } from './catalogue.ts'
import { ALLERGENS_BEING_VERIFIED, allergenLine, nameMarksIn, shownDescriptionIn, type MenuDish } from './model.ts'

/* ------------------------------------------------------------------ *
 * The allergen line, on a card and in the Összesítés
 * ------------------------------------------------------------------ */

/** `allergenLine` in `model.ts` decides; this only draws it. A verified dish
 *  prints its list or *nincs*; every other dish reads *Allergének ellenőrzés
 *  alatt* and nothing else. */
export function AllergenText(props: { page: PageBundle; dish: MenuDish | undefined }): JSX.Element {
  const { t } = props.page
  const line = allergenLine(props.dish)
  if (!line.verified) {
    return <span className="al-pending">{t(ALLERGENS_BEING_VERIFIED.hu, ALLERGENS_BEING_VERIFIED.en)}</span>
  }
  return (
    <>
      {t('allergének', 'allergens')}:{' '}
      <b className="num">{line.allergens.length > 0 ? line.allergens.join(', ') : t('nincs', 'none')}</b>
    </>
  )
}

/* ------------------------------------------------------------------ *
 * Plant-based look-alike names (run 10 item 7b)
 * ------------------------------------------------------------------ */

/**
 * A dish name. Run 12 item F (DECIDED 20261012): only a listed expression in
 * it is marked — italics, the asterisk directly after it — never the whole
 * name. `marks` are the publication's 0-based [start, end) pairs for this
 * name and language (`name_hu_marks` / `name_en_marks`), and the view the
 * name sits in carries `LookalikeFootnote` once. Customer views only.
 */
export function DishName(props: { label: string; marks: ReadonlyArray<readonly [number, number]> }): JSX.Element {
  if (props.marks.length === 0) return <>{props.label}</>
  return (
    <span className="lookalikename" data-lookalike="">
      <LookalikeText text={props.label} marks={props.marks} />
    </span>
  )
}

export const LOOKALIKE_FOOTNOTE = {
  hu: '* Növényi változat, állati eredetű összetevő nélkül.',
  en: '* Plant-based version, no animal ingredients.',
} as const

/**
 * Run 10 item 7c: a shown description, with every look-alike term in italics.
 * `marks` are the contract's 0-based [start, end) pairs for this text and
 * language. Since run 11 item 3 the menu card prints it (`DishDescription`).
 * Run 12 item 4c (DECIDED 20261011): each marked term carries its own
 * asterisk directly after it, and the view's footnote explains it.
 */
export function LookalikeText(props: { text: string; marks: ReadonlyArray<readonly [number, number]> }): JSX.Element {
  const { text, marks } = props
  const parts: ReactNode[] = []
  let at = 0
  for (const [start, end] of [...marks].sort((a, b) => a[0] - b[0])) {
    if (start < at || end > text.length) continue
    if (start > at) parts.push(text.slice(at, start))
    parts.push(
      <span className="lookalike" data-lookalike-term="" key={start}>
        <i>{text.slice(start, end)}</i>
        <span className="lookalikestar">*</span>
      </span>,
    )
    at = end
  }
  if (at < text.length) parts.push(text.slice(at))
  return <>{parts}</>
}

/**
 * Run 11 item 3: the shown description as one muted line under the dish
 * name, only when it is not blank. Menu cards only; the offer card has none.
 */
export function DishDescription(props: { dish: MenuDish; lang: Lang }): JSX.Element | null {
  const text = shownDescriptionIn(props.dish, props.lang)
  if (text === null) return null
  return (
    <span className="desc" data-testid="dish-description" data-desc={props.dish.id}>
      <LookalikeText text={text} marks={props.dish.descriptionMarks[props.lang]} />
    </span>
  )
}

/** The footnote, once per view, and only when something in the view carries an
 *  asterisk: a marked name or, since run 12 item 4d, a marked term. */
export function LookalikeFootnote(props: { page: PageBundle; show: boolean; id: string }): JSX.Element | null {
  if (!props.show) return null
  return (
    <p className="sub lookalikenote" id={props.id} data-testid="lookalike-footnote">
      {props.page.t(LOOKALIKE_FOOTNOTE.hu, LOOKALIKE_FOOTNOTE.en)}
    </p>
  )
}

/** The calm plant-based tag of run 10 item 7a, the same in every place.
 *  The claim rule of 20261019 takes it from the bare *100% növényi* to the
 *  predicate form; English is *100% plant-based* in every position. */
export function PlantBasedTag(props: { page: PageBundle; id: string }): JSX.Element {
  return (
    <span className="pbtag" id={props.id} data-testid="plant-based-tag">
      {props.page.t('100%-ban növényi', '100% plant-based')}
    </span>
  )
}

/* ------------------------------------------------------------------ *
 * A row of discrete choices (.opts)
 * ------------------------------------------------------------------ */

export interface Choice<T extends string> {
  value: T
  label: string
  /** The example line under a choice, e.g. *pl. networking*. */
  example?: string | null
  /** The count beside a filter chip (§5.3). */
  pill?: number
  disabled?: boolean
}

export function Choices<T extends string>(props: {
  id?: string
  className?: string
  choices: readonly Choice<T>[]
  value: T | null
  onChoose: (value: T) => void
  dataAttribute?: string
}): JSX.Element {
  const { id, className, choices, value, onChoose, dataAttribute } = props
  return (
    <div className={className ?? 'opts'} id={id}>
      {choices.map((choice) => {
        const extra = dataAttribute === undefined ? {} : { [`data-${dataAttribute}`]: choice.value }
        return (
          <button
            key={choice.value}
            type="button"
            aria-pressed={value === choice.value}
            disabled={choice.disabled === true}
            onClick={() => onChoose(choice.value)}
            {...extra}
          >
            {choice.label}
            {choice.pill !== undefined && choice.pill > 0 ? (
              <span className="pill num">{choice.pill}</span>
            ) : null}
            {choice.example !== undefined && choice.example !== null ? (
              <small>{choice.example}</small>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Notes (.note)
 * ------------------------------------------------------------------ */

export type NoteTone = 'flag' | 'stop' | 'ok' | 'calm'

export function Note(props: { tone?: NoteTone; id?: string; children: ReactNode }): JSX.Element {
  const tone = props.tone ?? 'flag'
  const className = tone === 'flag' ? 'note' : `note ${tone}`
  return (
    <div className={className} id={props.id}>
      {props.children}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * The thumbnail (§5.8)
 * ------------------------------------------------------------------ */

/**
 * The rendered width of the dish card's square photo at each breakpoint
 * (the three-column grid of the redesign: about 240 px at 1366 and about
 * 160 px at 390; the run prompt's estimate was 330 and 175), so the
 * browser picks the 400 or the 800 candidate at the device's density.
 *
 * photo_sizes (20261102): from 1 440px the stage widens towards 1 600px
 * (DECIDED 20261023) and the photo with it, 307px at 1 440 and 361px from
 * 1 600, where this said 260. Measured on production: 227px from 1 021 to
 * 1 439px, so 230 there; 362 above. The page's e2e
 * `dish-photo-sizes.spec.ts` holds the value to the measured box.
 */
export const DISH_PHOTO_SIZES =
  '(max-width: 600px) 46vw, (max-width: 1020px) 30vw, (max-width: 1439px) 230px, 362px'

/**
 * A photograph where there is one, the drawn category tile otherwise. The
 * photograph is the publication's (DECIDED 20261101): a square centre crop,
 * 400 and 800 px, in a `srcset`, with its size set so nothing shifts, and
 * loaded lazily below the first row only.
 */
export function Thumb(props: {
  dish: MenuDish
  label: string
  onZoom?: (dishId: string) => void
  /** The first row of cards loads at once; the rest lazily. */
  eager?: boolean
}): JSX.Element {
  const photo = props.dish.photo
  if (photo === null) {
    return (
      <span className="thumb" aria-hidden="true">
        <svg
          viewBox="0 0 40 40"
          dangerouslySetInnerHTML={{
            __html: CATEGORY_TILES[props.dish.category] ?? CHEF_TILE,
          }}
        />
      </span>
    )
  }
  return (
    <span
      className="thumb"
      data-zoom={props.dish.id}
      role="img"
      aria-label={props.label}
      onClick={
        props.onZoom === undefined
          ? undefined
          : (event) => {
              event.preventDefault()
              event.stopPropagation()
              props.onZoom?.(props.dish.id)
            }
      }
    >
      <img
        src={photo.card}
        srcSet={`${photo.thumb} 400w, ${photo.card} 800w`}
        sizes={DISH_PHOTO_SIZES}
        width={800}
        height={800}
        alt={props.label}
        loading={props.eager === true ? 'eager' : 'lazy'}
        decoding="async"
      />
    </span>
  )
}

/* ------------------------------------------------------------------ *
 * A dish row (.pick)
 * ------------------------------------------------------------------ */

export interface PickRowProps {
  dish: MenuDish
  label: string
  /** The category word and the allergen line under the name. */
  meta: ReactNode
  selected: boolean
  disabled: boolean
  /**
   * `pick` is the customer's own choice, `fruit` the fruit block, and
   * `gfauto` a dish Planteen included for the gluten-free guests — which is
   * shown ticked and is not the customer's to untick.
   */
  kind: 'pick' | 'fruit' | 'gfauto'
  /** Run 15 item 1(d): the customer set this dish's quantity with the stepper. */
  changed?: boolean
  title?: string
  onToggle?: () => void
  onZoom?: (dishId: string) => void
  /** Run 10 item 7c: the language shown, which decides the look-alike mark. */
  lang: Lang
  /** DECIDED 20261101: the first row of cards loads its photo at once. */
  eager?: boolean
  children?: ReactNode
}

export function PickRow(props: PickRowProps): JSX.Element {
  const { dish, label, meta, selected, disabled, kind, changed, title, onToggle, onZoom, lang } = props
  const classes = ['pick']
  if (kind === 'gfauto') classes.push('gfauto')
  if (changed === true) classes.push('emph')
  const activate = (): void => {
    if (disabled || kind === 'gfauto') return
    onToggle?.()
  }
  const dataAttribute =
    kind === 'pick'
      ? { 'data-pick': dish.id }
      : kind === 'fruit'
        ? { 'data-fruit': dish.id }
        : { 'data-gfauto': dish.id }
  return (
    <div
      className={classes.join(' ')}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-disabled={disabled ? true : undefined}
      title={title}
      onClick={activate}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        activate()
      }}
      {...dataAttribute}
    >
      <Thumb dish={dish} label={label} onZoom={onZoom} eager={props.eager} />
      <span>
        <span className="nm">
          <DishName label={label} marks={nameMarksIn(dish, lang)} />
        </span>
        <DishDescription dish={dish} lang={lang} />
        <span className="meta">{meta}</span>
        {props.children}
      </span>
      <span className="tick" aria-hidden="true">
        {selected ? '✓' : ''}
      </span>
    </div>
  )
}
